import { ArrowRight, Check, Lock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { usd } from "@/lib/purchases";

const Price = ({ pb, testid, className = "text-3xl" }) => (
  <span className={`font-mono font-bold text-white ${className}`} data-testid={testid}>{pb ? usd(pb) : "…"}</span>
);

const Features = ({ items }) => (
  <ul className="mt-5 space-y-2">
    {items.map((f) => (
      <li key={f} className="flex gap-2 text-sm text-slate-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />{f}</li>
    ))}
  </ul>
);

const SoonButton = ({ testid, label }) => (
  <button type="button" disabled className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 py-2.5 text-sm font-semibold text-slate-400 cursor-not-allowed" data-testid={testid} title="Payments are not live yet">
    <Lock className="h-4 w-4" aria-hidden="true" /> {label} — Coming soon
  </button>
);

const AddOn = ({ name, pb, testid }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
    <div className="min-w-0">
      <div className="text-xs font-semibold text-white">{name}</div>
      <div className="text-[11px] text-slate-500">One-time unlock · secure checkout</div>
    </div>
    <Price pb={pb} testid={testid} className="shrink-0 text-sm" />
  </div>
);

export const TierCards = ({ onStage }) => {
  const { priceFor } = useApp();
  const elite = priceFor("ELITE_CONCIERGE");
  return (
    <section className="py-12" aria-labelledby="tiers-title" data-testid="tiers-section">
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-300">Commercial Tiers</p>
      <h2 id="tiers-title" className="mt-2 text-base font-bold text-white md:text-lg">Start free. Pay only when the evidence says go.</h2>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <article className="iv-glass flex flex-col rounded-2xl p-6" data-testid="tier-card-free">
          <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-300">Free Explorer</h3>
          <div className="mt-3 flex items-baseline gap-2"><Price pb={priceFor("FREE_EXPLORER")} testid="tier-free-price" /><span className="text-xs text-slate-400">on-screen · 1 Free Assessment</span></div>
          <Features items={["Full 360° Scorecard — 20 criteria", "Local, Country & Global lenses", "7-section 4-tile Bento teardown", "Copy-paste customer discovery scripts", "3D visual concept + Agency vs AI cost anchor", "Mobile-responsive test preview website"]} />
          <div className="mt-5 space-y-2">
            <AddOn name="Downloadable 20-Criteria PDF Report" pb={priceFor("PDF_REPORT")} testid="tier-free-pdf-price" />
            <AddOn name="Done-For-You Live Site + Custom Domain (+ optional hosting)" pb={priceFor("DFY_WEBSITE_LAUNCH")} testid="tier-free-dfy-price" />
          </div>
          <button type="button" onClick={() => onStage(2)} className="iv-cta mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold text-white" data-testid="tier-free-cta">
            Validate My Idea — Free <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </article>
        <article className="iv-glass relative flex flex-col rounded-2xl p-6 !border-[#2563EB]/50 shadow-[0_0_40px_rgba(37,99,235,0.15)]" data-testid="tier-card-growth">
          <span className="absolute -top-3 left-6 rounded-full bg-[#2563EB] px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Most founders</span>
          <h3 className="text-sm font-bold uppercase tracking-wider text-blue-300">Growth Tier</h3>
          <div className="mt-3 flex items-baseline gap-2"><Price pb={priceFor("GROWTH_DOSSIER")} testid="tier-growth-price" /><span className="text-xs text-slate-400">one-time</span></div>
          <Features items={["10-question founder intake", "16-chapter investor-grade dossier", "Top-down & bottom-up TAM-SAM-SOM", "M.A.N. buyer qualification", "24-month 3-scenario financial model", "5 validation experiments", "30/60/90-day + 12-month roadmap", "PDF export"]} />
          <div className="flex-1" />
          <SoonButton testid="tier-growth-buy-button" label="Get Growth Dossier" />
        </article>
        <article className="iv-glass flex flex-col rounded-2xl p-6 !border-violet-400/40" data-testid="tier-card-elite">
          <h3 className="text-sm font-bold uppercase tracking-wider text-violet-300">Elite Concierge</h3>
          <div className="mt-3 flex items-baseline gap-2"><Price pb={elite} testid="tier-elite-price" /><span className="text-xs text-slate-400">+ statutory fees</span></div>
          <Features items={["Everything in Growth", "Human advisory sessions", "CA/CS/Legal company registration concierge", "Investor readiness audit"]} />
          {elite?.statutory_fees_note && <p className="mt-4 text-[11px] leading-relaxed text-slate-500" data-testid="tier-elite-statutory-note">{elite.statutory_fees_note}</p>}
          <div className="flex-1" />
          <SoonButton testid="tier-elite-buy-button" label="Book Elite Concierge" />
        </article>
      </div>
      <p className="mt-3 text-[11px] text-slate-500" data-testid="payments-placeholder-note">Payments are not live yet — checkout opens soon (Razorpay for India, Skydo for international).</p>
    </section>
  );
};
